import { CopyOutlined, GiftOutlined, MailOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Input, message, Table, Tag } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { getReferrals, redeemReferral, resendInvitation, sendInvitation } from '../../services/referralApi';
import { ReferralsSkeleton } from '../Common/Skeletons';
import './ReferralsPage.css';

const ReferralsPage = () => {
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [data, setData] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getReferrals();
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch referrals:', error);
      message.error('Failed to load referrals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSendInvite = async () => {
    if (!inviteEmail) {
      message.warning('Please enter an email address');
      return;
    }

    try {
      setActionLoading(true);
      const response = await sendInvitation(inviteEmail);
      message.success(response.data.message);
      setInviteEmail('');
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (!data?.program?.referral_link) return;

    navigator.clipboard.writeText(data.program.referral_link);
    message.success('Referral link copied to clipboard!');
  };

  const handleResend = async (referralId) => {
    try {
      setActionLoading(true);
      const response = await resendInvitation(referralId);
      message.success(response.data.message);
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to resend invitation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRedeem = async (referralId) => {
    try {
      setActionLoading(true);
      const response = await redeemReferral(referralId);
      message.success(response.data.message);
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to redeem referral');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      PENDING: { color: 'orange', icon: '⏳', text: 'Pending' },
      PAYMENT_PENDING: { color: 'purple', icon: '💳', text: 'Payment Pending' },
      JOINED: { color: 'green', icon: '✅', text: 'Joined' },
      EXPIRED: { color: 'default', icon: '⚠️', text: 'Expired' },
    };
    const config = statusConfig[status] || statusConfig.PENDING;
    return <Tag color={config.color}>{config.icon} {config.text}</Tag>;
  };

  const getRedemptionStatusTag = (status) => {
    const statusConfig = {
      PENDING: { color: 'blue', text: 'Ready' },
      REDEEMED: { color: 'green', text: 'Redeemed' },
      FORFEITED: { color: 'red', text: 'Forfeited' },
    };
    const config = statusConfig[status] || statusConfig.PENDING;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: 'Friend',
      dataIndex: 'referee_email_masked',
      key: 'referee_email_masked',
      width: '25%',
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: '15%',
      render: (source) => (
        <span>{source === 'EMAIL' ? '📧 Email' : '🔗 Link'}</span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '20%',
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Date',
      dataIndex: 'invited_at',
      key: 'invited_at',
      width: '20%',
      render: (date) => {
        if (!date) return '-';
        const d = new Date(date);
        const now = new Date();
        const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 30) return `${diffDays}d ago`;
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: '20%',
      render: (_, record) => {
        if (record.my_redemption_status === 'REDEEMED') {
          return <span style={{ color: 'var(--text-secondary)' }}>+{record.credits_awarded} credits</span>;
        }
        if (record.my_redemption_status === 'FORFEITED') {
          return <span style={{ color: 'var(--text-secondary)' }}>Forfeited</span>;
        }
        if (record.can_redeem) {
          return (
            <Button
              type="primary"
              size="small"
              onClick={() => handleRedeem(record.id)}
              loading={actionLoading}
            >
              Redeem {record.credits_value || 500}
            </Button>
          );
        }
        if (record.status === 'PAYMENT_PENDING') {
          return <span style={{ color: 'var(--text-secondary)' }}>Awaiting payment</span>;
        }
        if (record.status === 'PENDING') {
          return (
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => handleResend(record.id)}
              loading={actionLoading}
            >
              Resend
            </Button>
          );
        }
        return '-';
      },
    },
  ];

  if (loading) {
    return <ReferralsSkeleton />;
  }

  const stats = data?.stats;
  const program = data?.program;

  return (
    <div className="referrals-page">
      {/* Premium Hero Section */}
      <div className="referrals-hero">
        <div className="hero-icon-container">
          <GiftOutlined />
        </div>
        <h1>Refer Friends, Earn Credits</h1>
        <p>
          Invite friends to CellCog and you'll both receive 500 credits
          added to your permanent wallet. Credits are granted after your friend makes their first purchase.
        </p>

      </div>

      {/* Invite Section */}
      <div className="invite-section">
        <h3>Invite a Friend</h3>
        <div className="invite-divider" />

        {/* Email Invitation */}
        <div className="invite-method">
          <div className="method-label">📧 Send Email Invitation</div>
          <div className="input-group">
            <Input
              placeholder="friend@example.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              onPressEnter={handleSendInvite}
              size="large"
              disabled={!program?.is_enabled}
            />
            <Button
              type="primary"
              size="large"
              icon={<MailOutlined />}
              onClick={handleSendInvite}
              loading={actionLoading}
              disabled={!program?.is_enabled}
            >
              Send Invite
            </Button>
          </div>
        </div>

        <div className="or-divider">— OR —</div>

        {/* Link Sharing */}
        <div className="invite-method">
          <div className="method-label">🔗 Share Your Referral Link</div>
          <div className="input-group">
            <Input
              value={program?.referral_link || ''}
              readOnly
              size="large"
              disabled={!program?.is_enabled}
            />
            <Button
              type="primary"
              size="large"
              icon={<CopyOutlined />}
              onClick={handleCopyLink}
              disabled={!program?.is_enabled}
            >
              Copy Link
            </Button>
          </div>
        </div>

        {!program?.is_enabled && (
          <div className="disabled-notice">
            ⚠️ Your referral program has been disabled. Please contact support if you believe this is an error.
          </div>
        )}
      </div>

      {/* Referred By Section (if user was referred) */}
      {data?.referred_by && (
        <div className="referred-by-section">
          <h3>You Were Referred By</h3>
          <div className="invite-divider" />
          <div className="referred-by-card">
            <div className="referred-by-info">
              <div className="referrer-display">{data.referred_by.referrer_display}</div>
              <div className="referrer-email">{data.referred_by.referrer_email_masked}</div>
              <div className="join-date">
                {data.referred_by.status === 'PAYMENT_PENDING' ? (
                  'Awaiting your first purchase'
                ) : data.referred_by.joined_at ? (
                  `Joined ${new Date(data.referred_by.joined_at).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}`
                ) : null}
              </div>
            </div>
            <div className="referred-by-action">
              {data.referred_by.my_redemption_status === 'REDEEMED' ? (
                <div className="redeemed-badge">
                  <GiftOutlined /> {data.referred_by.credits_awarded} credits redeemed
                </div>
              ) : data.referred_by.can_redeem ? (
                <Button
                  type="primary"
                  size="large"
                  onClick={() => handleRedeem(data.referred_by.id)}
                  loading={actionLoading}
                >
                  Redeem Your {data.referred_by.credits_value || 500} Credits
                </Button>
              ) : data.referred_by.status === 'PAYMENT_PENDING' ? (
                <div className="status-badge">
                  <Tag color="purple">💳 Make your first purchase to unlock {data.referred_by.credits_value || 500} credits</Tag>
                </div>
              ) : (
                <div className="status-badge">
                  {getRedemptionStatusTag(data.referred_by.my_redemption_status)}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Referrals Table */}
      <div className="referrals-table-section">
        <h3>Your Referrals</h3>
        <div className="invite-divider" />

        <Table
          dataSource={data?.my_referrals || []}
          columns={columns}
          rowKey="id"
          pagination={false}
          locale={{ emptyText: 'No referrals yet. Start inviting friends!' }}
          className="referrals-table"
        />

        {data?.my_referrals && data.my_referrals.length > 0 && (
          <div className="table-footer">
            Showing {data.my_referrals.length} referral{data.my_referrals.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Program Terms */}
      <div className="program-terms">
        <h4>Program Terms</h4>
        <div className="terms-divider" />
        <p>
          Fraudulent activity (such as self-referrals using multiple email addresses) may result in
          removal from the referral program and forfeiture of any unredeemed or redeemed referral credits.
        </p>
      </div>
    </div>
  );
};

export default ReferralsPage;
